import React, { useState, useEffect, useRef } from 'react';
import { formatClock } from './RestTimer';

// Total elapsed time in this session, ticking quietly in the header.
//
// Isolated exactly the way RestTimer is, for exactly the same reason — see its
// header comment. A tick that lived in ExecutionLogger's own state would
// re-render the whole screen every second, including the weight input an
// athlete is mid-keystroke on. Owning its own interval and its own state means
// a tick here re-renders only this span.
//
// Derived from a timestamp, not accumulated ticks, for the same reason
// RestTimer is: a locked phone throttles setInterval, and counting ticks would
// under-report however long the screen was actually locked.
//
// `running` (default true) lets a caller pause the clock — a pre-flight
// pause between series is setup time, not session time, and must not count.
// Pausing freezes the display; resuming shifts the start anchor forward by
// however long the pause lasted, so the SAME (Date.now() - start) formula
// above stays the one source of truth instead of growing a second
// accumulator that could drift out of sync with it.
const SessionClock = ({ startedAt, running = true }) => {
  const [elapsed, setElapsed] = useState(0);
  const start = useRef(startedAt);
  const pausedAt = useRef(null);

  useEffect(() => {
    if (!running) {
      // Freeze at the elapsed time as of THIS instant — not whatever the
      // last tick happened to show, which could be up to a second stale.
      setElapsed(Math.floor((Date.now() - start.current) / 1000));
      pausedAt.current = Date.now();
      return undefined;
    }

    if (pausedAt.current !== null) {
      start.current += Date.now() - pausedAt.current;
      pausedAt.current = null;
    }

    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start.current) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [running]);

  return (
    <span className="session-clock mono" aria-label={`Session time ${formatClock(elapsed)}`}>
      {formatClock(elapsed)}
    </span>
  );
};

export default SessionClock;
